import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../utils/api';
import { errorText } from '../utils/errorText';
import { badge, box } from '../pages/shared/styles';
import { MODEL_NAMES, REFERENCE_SOURCE_NAMES, modelName, orderedKeys, sourceName } from '../utils/aiModels';

/*
 * What is serving identifications right now, and what it learned from
 * (GET /ai/status, routes/review_queue.py::get_status).
 *
 * Two kinds of fact, kept apart on purpose:
 *   - the live service's own answer (attached or not, its model version, which
 *     models load and on which runtime) -- absent whenever no AI service is
 *     attached, which is the normal state of the cloud;
 *   - the committed reference-bank snapshot (reference_bank_summary.json): the
 *     bank's classes, images and sources, and the source weighting the next
 *     build will apply. A snapshot describes ONE model version, so when the
 *     service serves another, the card says so instead of letting the two
 *     read as one.
 * Shown to experts and system admins; anyone else gets a 403 and a short note.
 */
// Tiers in order of weight, highest first (backend/ai/source_weights.json).
const TIERS = { expert_curated: 1, local_israel: 1, external_benchmark: 1 };
const num = v => (v === null || v === undefined ? '—' : Number(v).toLocaleString());

export default function ServedModelCard() {
  const { t } = useTranslation('mlops');
  const [status, setStatus] = useState(null);
  const [state, setState] = useState('loading');
  const [error, setError] = useState('');

  useEffect(() => {
    let live = true;
    api.get('/ai/status')
      .then(({ data }) => { if (live) { setStatus(data || {}); setState('ready'); } })
      .catch((e) => {
        if (!live) return;
        if (e?.response?.status === 403) setState('forbidden');
        else { setError(errorText(e)); setState('error'); }
      });
    return () => { live = false; };
  }, []);

  if (state === 'loading') {
    return <section style={box} data-testid="served-model"><p style={muted}>{t('served.loading')}</p></section>;
  }
  if (state === 'forbidden') {
    return <p style={muted} data-testid="served-model-forbidden">{t('served.notPermitted')}</p>;
  }
  if (state === 'error') {
    return (
      <section style={box} data-testid="served-model">
        <p role="alert" style={{ color: 'var(--c-danger-fg)', margin: 0 }}>{t('served.loadFailed', { error })}</p>
      </section>
    );
  }

  const service = status.service || null;
  const snap = status.reference_bank || null;
  const bank = snap?.bank || service?.reference_bank || null;
  const dataset = snap?.dataset || null;
  const weighting = snap?.source_weighting || null;
  const matches = status.matches_served || {};
  const models = orderedKeys([...Object.keys(service?.models_available || {}),
    ...Object.keys(service?.runtimes || {})], MODEL_NAMES);

  return (
    <section style={box} data-testid="served-model">
      <h4 style={{ marginTop: 0 }}>{t('served.title')}</h4>
      <p style={muted}>{t('served.intro')}</p>

      {/* The service */}
      <div style={row}>
        <span style={badge(status.attached ? 'success' : 'warning')} data-testid="served-attached">
          {status.attached ? t('served.attached') : t('served.detached')}
        </span>
        {service?.status === 'degraded' && <span style={badge('warning')}>{t('served.degraded')}</span>}
        {status.served_version && (
          <span style={{ fontSize: 13 }}>
            {t('served.servedVersion')}: <code data-testid="served-version">{status.served_version}</code>
          </span>
        )}
        {service && typeof service.weights_fitted === 'boolean' && (
          <span style={{ fontSize: 13 }}>
            {service.weights_fitted ? t('served.weightsFitted') : t('served.weightsNotFitted')}
          </span>
        )}
      </div>
      {!status.attached && status.reason && (
        <p style={{ fontSize: 13, margin: '4px 0' }} data-testid="served-reason">
          {t(`served.reason.${status.reason}`, { defaultValue: status.reason })}
        </p>
      )}
      {!status.attached && status.detail && (
        <p style={muted} data-testid="served-detail">{t('served.detachedWhy')}: <span dir="ltr">{status.detail}</span></p>
      )}
      {status.served_version && typeof status.registered === 'boolean' && (
        <p style={muted} data-testid="served-registered">
          {status.registered ? t('served.registered') : t('served.notRegistered')}
        </p>
      )}
      {models.length > 0 && (
        <table style={table} data-testid="served-models">
          <thead><tr>
            <th style={cell}>{t('served.colModel')}</th>
            <th style={cell}>{t('served.colAvailable')}</th>
            <th style={cell}>{t('served.colRuntime')}</th>
          </tr></thead>
          <tbody>{models.map((m) => {
            const available = service.models_available?.[m];
            return (
              <tr key={m} data-testid={`served-model-${m}`}>
                <td style={cell}>{modelName(m)}</td>
                <td style={cell}>
                  <span style={badge(available ? 'success' : 'danger')}>
                    {available ? t('served.available') : t('served.unavailable')}
                  </span>
                </td>
                <td style={cell}>{service.runtimes?.[m] ? t(`served.runtime.${service.runtimes[m]}`,
                  { defaultValue: service.runtimes[m] }) : '—'}</td>
              </tr>
            );
          })}</tbody>
        </table>
      )}

      {/* How good this model was measured to be (reference_bank_summary.json `evaluation`) */}
      {snap?.evaluation && (
        <>
          <h4 style={h4}>{t('served.evaluationTitle')}</h4>
          <p style={{ fontSize: 13, margin: '0 0 4px' }} data-testid="served-evaluation">
            {t('served.evaluationValue', { top1: pctOf(snap.evaluation.top1), top5: pctOf(snap.evaluation.top5),
              ece: numOf(snap.evaluation.ece_after), eceBefore: numOf(snap.evaluation.ece_before),
              n: num(snap.evaluation.n) })}
          </p>
          <p style={muted}>{t('served.evaluationNote', { version: snap.model_version || '?' })}</p>
          {Object.keys(snap.evaluation.probes || {}).length > 0 && (
            <table style={table} data-testid="served-probes">
              <thead><tr>
                <th style={cell}>{t('served.colModel')}</th>
                <th style={cell}>{t('served.colProbe')}</th>
                <th style={cell}>{t('served.colKnn')}</th>
              </tr></thead>
              <tbody>{orderedKeys(Object.keys(snap.evaluation.probes), MODEL_NAMES).map((m) => {
                const p = snap.evaluation.probes[m] || {};
                return (
                  <tr key={m} data-testid={`served-probe-${m}`}>
                    <td style={cell}>{modelName(m)}</td>
                    <td style={cell}>{pctOf(p.genus_top1)} / {pctOf(p.genus_top5)}</td>
                    <td style={cell}>{pctOf(p.knn_genus_top1)} / {pctOf(p.knn_genus_top5)}</td>
                  </tr>
                );
              })}</tbody>
            </table>
          )}
        </>
      )}

      {/* The reference bank */}
      <h4 style={h4}>{t('served.bankTitle')}</h4>
      {matches.reference_bank === false && (
        <p role="note" style={warning} data-testid="served-bank-mismatch">
          {t('served.bankMismatch', { snapshot: snap?.model_version || '?', served: status.served_version })}
        </p>
      )}
      {!bank ? <p style={muted}>{t('served.noBank')}</p> : (
        <>
          {snap?.model_version && (
            <p style={muted}>{t('served.snapshotOf', { version: snap.model_version,
              date: (snap.generated_at || '').slice(0, 10) || '—' })}</p>
          )}
          <dl style={facts} data-testid="served-bank">
            <dt style={dt}>{t('served.bankModels')}</dt>
            <dd style={dd}>{(bank.models || []).map(modelName).join(', ') || '—'}</dd>
            {bank.search && (
              <>
                <dt style={dt}>{t('served.search')}</dt>
                <dd style={dd} data-testid="served-search">{t('served.searchValue', { metric: bank.search.metric,
                  k: bank.search.k, temperature: bank.search.temperature })}</dd>
              </>
            )}
            <dt style={dt}>{t('served.classes')}</dt>
            <dd style={dd}>{num(bank.class_count)}</dd>
            <dt style={dt}>{t('served.images')}</dt>
            <dd style={dd}>{num(bank.image_count)}</dd>
            <dt style={dt}>{t('served.excluded')}</dt>
            <dd style={dd} data-testid="served-excluded">{t('served.excludedValue', { n: num(bank.excluded_class_count),
              min: bank.min_images_per_class ?? '—' })}</dd>
            <dt style={dt}>{t('served.dataset')}</dt>
            <dd style={dd}><code>{dataset?.name || bank.dataset || '—'}</code></dd>
          </dl>
          {dataset && <SourceTable t={t} dataset={dataset} />}
        </>
      )}

      {/* Source weighting */}
      {weighting && (
        <>
          <h4 style={h4}>{t('served.weightingTitle')}</h4>
          <p style={{ fontSize: 13, margin: '0 0 6px' }} data-testid="served-weighting-applied">
            {weighting.applied_in_served_bank ? t('served.weightingApplied') : t('served.weightingNotApplied')}
          </p>
          <table style={table} data-testid="served-tiers">
            <thead><tr>
              <th style={cell}>{t('served.colTier')}</th>
              <th style={cell}>{t('served.colMultiplier')}</th>
              <th style={cell}>{t('served.colNextBuild')}</th>
            </tr></thead>
            <tbody>{orderedKeys(Object.keys(weighting.tiers || {}), TIERS)
              .map(k => (
                <tr key={k} data-testid={`served-tier-${k}`}>
                  <td style={cell}>{t(`served.tier.${k}`, { defaultValue: k })}</td>
                  <td style={cell}>×{Number(weighting.tiers[k]).toFixed(1)}</td>
                  <td style={cell}>{num(weighting.images_per_tier_at_next_build?.[k] ?? 0)}</td>
                </tr>
              ))}</tbody>
          </table>
          {Object.keys(weighting.missing_member_manifests || {}).length > 0 && (
            <p style={warning}>{t('served.missingManifests', {
              names: Object.keys(weighting.missing_member_manifests).join(', ') })}</p>
          )}
        </>
      )}

      {/* Calibration */}
      {status.calibration && (
        <p style={{ ...muted, marginTop: 10 }} data-testid="served-calibration">
          {t('served.calibration', { n: num(status.calibration.n), level: status.calibration.level || '—',
            version: status.calibration.model_version || '?' })}
        </p>
      )}
      {matches.calibration === false && (
        <p role="note" style={warning} data-testid="served-calibration-mismatch">
          {t('served.calibrationMismatch', { snapshot: status.calibration?.model_version || '?',
            served: status.served_version })}
        </p>
      )}

      {/* What the registry holds (ai_model_versions), newest first */}
      <h4 style={h4}>{t('served.recordedTitle')}</h4>
      {(status.recorded_versions || []).length === 0
        ? <p style={muted} data-testid="served-recorded-empty">{t('served.recordedEmpty')}</p>
        : (
          <table style={table} data-testid="served-recorded">
            <thead><tr>
              <th style={cell}>{t('served.colVersion')}</th>
              <th style={cell}>{t('served.colStage')}</th>
              <th style={cell}>{t('served.colFirstSeen')}</th>
              <th style={cell}>{t('served.colLastSeen')}</th>
            </tr></thead>
            <tbody>{status.recorded_versions.map((v) => (
              <tr key={v.model_version} data-testid={`served-recorded-${v.model_version}`}>
                <td style={cell}>
                  <code>{v.model_version}</code>
                  {v.model_version === status.served_version && <> <span style={badge('success')}>{t('served.serving')}</span></>}
                </td>
                <td style={cell}>{t(`stage_${v.stage}`, { defaultValue: v.stage })}</td>
                <td style={cell}>{(v.first_seen_at || '').slice(0, 10) || '—'}</td>
                <td style={cell}>{(v.last_seen_at || '').slice(0, 10) || '—'}</td>
              </tr>
            ))}</tbody>
          </table>
        )}

      <p style={{ ...muted, marginBottom: 0 }} data-testid="served-local-only">{t('served.localOnly')}</p>
    </section>
  );
}

const pctOf = v => (typeof v === 'number' ? `${(v * 100).toFixed(1)}%` : '—');
const numOf = v => (typeof v === 'number' ? v.toFixed(3) : '—');

/** Images in the bank and observations in the dataset, per reference source, with a bar each. */
function SourceTable({ t, dataset }) {
  const images = dataset.bank_images_per_source || {};
  const observations = dataset.observations_per_source || {};
  const keys = orderedKeys([...Object.keys(images), ...Object.keys(observations)], REFERENCE_SOURCE_NAMES);
  const max = Math.max(1, ...keys.map(k => images[k] || 0));
  return (
    <>
      {keys.length > 0 && (
        <table style={table} data-testid="served-sources">
          <thead><tr>
            <th style={cell}>{t('served.colSource')}</th>
            <th style={cell}>{t('served.colObservations')}</th>
            <th style={cell}>{t('served.colBankImages')}</th>
            <th style={{ ...cell, width: '35%' }} aria-hidden="true" />
          </tr></thead>
          <tbody>{keys.map(k => (
            <tr key={k} data-testid={`served-source-${k}`}>
              <td style={cell}>{sourceName(k)}</td>
              <td style={cell}>{num(observations[k])}</td>
              <td style={cell}>{num(images[k])}</td>
              <td style={cell} aria-hidden="true">
                <div style={{ ...bar, width: `${Math.round(((images[k] || 0) / max) * 100)}%` }} />
              </td>
            </tr>
          ))}</tbody>
        </table>
      )}
      {dataset.available_here === false
        ? <p style={muted}>{t('served.datasetNotHere')}</p>
        : (
          <p style={muted} data-testid="served-dataset-totals">{t('served.datasetTotals', {
            taxa: num(dataset.taxa), observations: num(dataset.observations), images: num(dataset.images_on_disk) })}</p>
        )}
    </>
  );
}

const muted = { fontSize: 12, color: 'var(--c-text-muted-fg)', margin: '4px 0' };
const row = { display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginBottom: 6 };
const h4 = { margin: '14px 0 6px' };
const table = { borderCollapse: 'collapse', fontSize: 13, width: '100%', marginTop: 6 };
const cell = { padding: '3px 8px', borderBottom: '1px solid var(--c-border)', textAlign: 'start' };
const facts = { display: 'grid', gridTemplateColumns: 'max-content 1fr', gap: '2px 12px', fontSize: 13, margin: '6px 0' };
const dt = { color: 'var(--c-text-muted-fg)' };
const dd = { margin: 0 };
const bar = { height: 8, borderRadius: 4, background: 'var(--c-primary)', minWidth: 2 };
const warning = {
  fontSize: 13, margin: '6px 0', padding: '6px 10px', borderRadius: 6,
  background: 'var(--c-warning-soft)', color: 'var(--c-warning-text-fg)',
};
